import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import http from '../../../utils/http';
import ApiConstants from '../../../constants/apiConstants';
import appConstants from '../../../constants/appConstants';
import { AUTH_RESEND_COOLDOWN_SEC } from '../../../constants/authConstants';
import { getAuthContextFromSearch, getRequestErrorMessage, isValidEmail } from '../../../utils/authUtils';
import {
  AuthActionsStack,
  AuthAlertMessage,
  AuthBackToSignIn,
  AuthForm,
  AuthHead,
  AuthMutedFoot,
  AuthPage,
  AuthPrimaryButton,
  AuthSecondaryButton,
  AuthStatusBlock,
  AuthTextField,
  buildEmailHighlightSubtitle,
} from '../../../components/auth/authUi';

const strings = appConstants.auth.forgotPassword;

export default function ForgotPassword() {
  const [searchParams] = useSearchParams();
  const authContext = useMemo(
    () => getAuthContextFromSearch(searchParams),
    [searchParams],
  );
  const [email, setEmail] = useState('');
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const emailError =
    touched && email.trim().length > 0 && !isValidEmail(email)
      ? strings.errors.invalidEmail
      : touched && email.trim().length === 0
        ? strings.errors.emailRequired
        : null;

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timer = window.setInterval(() => {
      setResendCooldown((seconds) => (seconds <= 1 ? 0 : seconds - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [resendCooldown]);

  async function sendResetLink(targetEmail) {
    await http.post(
      ApiConstants.AUTH.FORGOT_PASSWORD.URL,
      {},
      {
        email: targetEmail.trim()
      },
    );
  }

  async function onSubmit(event) {
    event.preventDefault();
    setSubmitError(null);
    setTouched(true);
    setSuccess(false);

    if (!isValidEmail(email)) return;

    try {
      setSubmitting(true);
      await sendResetLink(email);
      setSuccess(true);
      setResendCooldown(AUTH_RESEND_COOLDOWN_SEC);
    } catch (err) {
      setSubmitError(getRequestErrorMessage(err, strings.errors.sendFailed));
    } finally {
      setSubmitting(false);
    }
  }

  async function onResend() {
    if (resendCooldown > 0 || submitting || !isValidEmail(email)) return;
    setSubmitError(null);
    try {
      setSubmitting(true);
      await sendResetLink(email);
      setResendCooldown(AUTH_RESEND_COOLDOWN_SEC);
    } catch (err) {
      setSubmitError(getRequestErrorMessage(err, strings.errors.sendFailed));
    } finally {
      setSubmitting(false);
    }
  }

  function tryDifferentEmail() {
    setSuccess(false);
    setSubmitError(null);
    setTouched(false);
    setResendCooldown(0);
  }

  if (success) {
    const resendLabel =
      resendCooldown > 0
        ? strings.resendCooldown.replace('{seconds}', String(resendCooldown))
        : strings.resend;

    return (
      <AuthPage>
        <AuthStatusBlock
          variant="mail"
          title={strings.successTitle}
          subtitle={buildEmailHighlightSubtitle(strings.successSubtitle, email.trim())}
          expiry={strings.successExpiry}
        />
        <AuthActionsStack>
          <AuthSecondaryButton
            disabled={resendCooldown > 0 || submitting}
            onClick={onResend}
          >
            {resendLabel}
          </AuthSecondaryButton>
          <AuthBackToSignIn />
        </AuthActionsStack>
        {submitError ? <AuthAlertMessage message={submitError} /> : null}
        <AuthMutedFoot
          prompt={strings.wrongEmailPrompt}
          linkLabel={strings.tryDifferentEmail}
          onLinkClick={tryDifferentEmail}
        />
      </AuthPage>
    );
  }

  return (
    <AuthPage>
      <AuthHead title={strings.title} subtitle={strings.subtitle} />
      <AuthForm onSubmit={onSubmit}>
        <AuthTextField
          id="email"
          label={strings.emailLabel}
          type="email"
          value={email}
          onChange={(value) => {
            setEmail(value);
            setSubmitError(null);
          }}
          onBlur={() => setTouched(true)}
          placeholder={strings.emailPlaceholder}
          error={emailError ?? submitError}
          autoComplete="email"
          inputMode="email"
        />
        <AuthPrimaryButton disabled={submitting}>
          {submitting ? strings.submitLoading : strings.submit}
        </AuthPrimaryButton>
        <AuthBackToSignIn />
      </AuthForm>
    </AuthPage>
  );
}